// src/redux/slices/withdrawalsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { showNotification } from './notificationSlice';
import API_ROUTES from "../../config/apiRoutes";

// Fetch all withdrawals with filters and pagination
export const fetchWithdrawals = createAsyncThunk(
  'withdrawals/fetchWithdrawals',
  async (filterParams = {}, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const payload = {
        currentPage: filterParams.currentPage || 1,
        limit: filterParams.limit || 10,
        ...filterParams
      };

      console.log('Withdrawals API Request Payload:', payload);

      const response = await axios.post(
        API_ROUTES.Withdrawals.GET_ALL,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log('Withdrawals API Response:', response.data);

      // Handle response structure
      const responseData = response.data;
      let withdrawals = [];
      let total = 0;
      let totalPages = 1;

      if (responseData.data && Array.isArray(responseData.data)) {
        withdrawals = responseData.data;
        total = responseData.total || responseData.data.length;
        totalPages = responseData.totalPages || Math.ceil(total / payload.limit);
      } else if (Array.isArray(responseData)) {
        withdrawals = responseData;
        total = responseData.length;
        totalPages = Math.ceil(total / payload.limit);
      }

      console.log('Processed withdrawals data:', { withdrawals: withdrawals.length, total, totalPages });

      return {
        withdrawals,
        total,
        totalPages,
        currentPage: payload.currentPage
      };
    } catch (error) {
      console.error('Withdrawals API Error:', error);
      return rejectWithValue(error.response?.data?.message || "Failed to fetch withdrawals");
    }
  }
);

// Update withdrawal status
export const updateWithdrawalStatus = createAsyncThunk(
  'withdrawals/updateWithdrawalStatus',
  async ({ withdrawalId, formData }, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.patch(
        API_ROUTES.Withdrawals.UPDATE(withdrawalId),
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      
      // Get the status from formData for the success message
      const status = formData.get('status');
      dispatch(showNotification({ 
        message: `Withdrawal ${status} successfully!`, 
        type: "success" 
      }));
      
      return { 
        withdrawalId, 
        status, 
        remarks: formData.get('remarks') 
      };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update withdrawal status";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

// Delete withdrawal
export const deleteWithdrawal = createAsyncThunk(
  'withdrawals/deleteWithdrawal',
  async (withdrawalId, { getState, dispatch, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.delete(
        API_ROUTES.Withdrawals.DELETE(withdrawalId),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      dispatch(showNotification({ 
        message: "Withdrawal deleted successfully!", 
        type: "success" 
      }));
      return withdrawalId;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete withdrawal";
      dispatch(showNotification({ message, type: "error" }));
      return rejectWithValue(message);
    }
  }
);

const withdrawalsSlice = createSlice({
  name: 'withdrawals',
  initialState: {
    withdrawals: [],
    total: 0,
    totalPages: 1,
    currentPage: 1,
    loading: false,
    error: null,
    updating: false,
    deleting: false,
  },
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch withdrawals
      .addCase(fetchWithdrawals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawals.fulfilled, (state, action) => {
        state.loading = false;
        state.withdrawals = action.payload.withdrawals;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchWithdrawals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update withdrawal status
      .addCase(updateWithdrawalStatus.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateWithdrawalStatus.fulfilled, (state, action) => {
        state.updating = false;
        const { withdrawalId, status, remarks } = action.payload;
        state.withdrawals = state.withdrawals.map(withdrawal =>
          withdrawal._id === withdrawalId 
            ? { ...withdrawal, status, remarks, updatedAt: new Date().toISOString() } 
            : withdrawal
        );
      })
      .addCase(updateWithdrawalStatus.rejected, (state) => {
        state.updating = false;
      })
      
      // Delete withdrawal
      .addCase(deleteWithdrawal.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteWithdrawal.fulfilled, (state, action) => {
        state.deleting = false;
        state.withdrawals = state.withdrawals.filter(
          withdrawal => withdrawal._id !== action.payload
        );
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteWithdrawal.rejected, (state) => {
        state.deleting = false;
      });
  },
});

export const { setCurrentPage, clearError } = withdrawalsSlice.actions;
export default withdrawalsSlice.reducer;